import React, { useEffect, useState } from 'react';

import { Grid,Box } from "@material-ui/core";
import { Button } from "../../../components/Wrappers";
import Chip from '@material-ui/core/Chip';

import MUIDataTable from "../../../components/DataTable"
import AddToolButton from "../../../components/DataTable/AddToolButton"
import {useApiHttpState} from '../../../context/ApiContext'
import useListLoad from "../../../hooks/useListLoad"
import { useAddSnackbarQueue} from "../../../context/SnackbarContext";
import { useOpenDialog , useOpenConfirmationDialog, useCloseDialog } from "../../../context/DialogContext";
import { useStoreHelpers } from '../../../store'
import { useHelperStyles } from '../../../styles'

import PageTitle from "../../../components/PageTitle";
import UpsertForm from './UpsertForm'
import InviteLink from './InviteLink'

// The server sends the useful text in response.data.message.
const errorMessage = er =>
  (er && er.response && er.response.data && er.response.data.message) || (er && er.message) || 'Unknown error'

// inviteStatus comes from the server so the client never sees the raw token or
// password hash. "none" covers accounts created before invitations existed.
const INVITE_LABELS = {
  passwordSet: { label: "Password set", color: "primary" },
  pending: { label: "Invitation pending", color: "secondary" },
  expired: { label: "Invitation expired", color: "default" },
  none: { label: "No password", color: "default" },
}

  export default function Users() { 
    const  {getItemsArray : getItems , addItems, addItem } = useStoreHelpers()  
    const openDialog = useOpenDialog()
    const openConfirmationDialog = useOpenConfirmationDialog()
    const helperClasses = useHelperStyles()
    const close = useCloseDialog()
    const addSnackbarQueue = useAddSnackbarQueue() 
    const {api} = useApiHttpState()
    // Ids with an invite request in flight, so the buttons can be disabled
    // individually instead of blocking the whole table.
    const [pendingInviteIds, setPendingInviteIds] = useState([])
    const {isLoading, isError} = useListLoad(api, '/users/withRoles', data => addItems(data, 'users'))
    const loadRolesData = async () => {
      const [er,response] = await api.get('/roles/',{useLoader:false})
      if(!er) {
        addItems(response.data,"roles")
      }      
    }  
    const users = getItems("users")
    const roles = getItems("roles")
    //Roles load in parallel with users and the store returns null until they
    //arrive, so this is guarded before being handed to the form. Appointment
    //Handler is excluded: that membership is owned by appointment settings.
    const assignableRoles = (roles || []).filter(role => role.name !== 'Appointment Handler')
    const toggleUserStatus = async (user) => {
      //Dedicated admin-only route: the generic PUT /users/:id accepts any column.
      const [er,response] = await api.put(`/users/${user.id}/active`,{active: !user.active ? 1 : 0})
      if(!er) {
        addItem(response.data,"users")   
        close()     
        addSnackbarQueue(`The user ${user.email} was ${!user.active ? "enabled" : "disabled"} successfully.`,"success")  
      } else {
        addSnackbarQueue("An error occurred " + errorMessage(er),"error")  
      }
    }

    const markPending = (id, isPending) => setPendingInviteIds(prev =>
      isPending ? [...prev, id] : prev.filter(pendingId => pendingId !== id))

    // Issue a fresh invitation and email it again.
    const resendInvite = async (user) => {
      markPending(user.id, true)
      const [er,response] = await api.post(`/users/${user.id}/invite/resend`)
      markPending(user.id, false)
      if(er) {
        addSnackbarQueue("An error occurred " + errorMessage(er),"error")
        return
      }
      const { user: updatedUser, inviteLink, emailSent, emailError } = response.data
      addItem(updatedUser,"users")
      addSnackbarQueue(
        emailSent ? `A new invitation was emailed to ${updatedUser.email}.` : `The invitation email to ${updatedUser.email} failed to send.`,
        emailSent ? "success" : "error",
      )
      openDialog("Invitation Link", <InviteLink inviteLink={inviteLink} emailSent={emailSent} emailError={emailError} email={updatedUser.email} />)
    }

    // Hand the admin the link without sending mail. Returns the invitation
    // already in flight when it is still valid, so this does not break a link
    // that was emailed earlier.
    const showInviteLink = async (user) => {
      markPending(user.id, true)
      const [er,response] = await api.get(`/users/${user.id}/inviteLink`)
      markPending(user.id, false)
      if(er) {
        addSnackbarQueue("An error occurred " + errorMessage(er),"error")
        return
      }
      const { user: updatedUser, inviteLink } = response.data
      addItem(updatedUser,"users")
      //emailSent omitted on purpose: nothing was emailed on this path.
      openDialog("Invitation Link", <InviteLink inviteLink={inviteLink} email={updatedUser.email} />)
    }

    useEffect(() => {
      loadRolesData()
    },[])
    
    const columns = [ "email", "firstName", "lastName", "cellphone",
      {name:"roles", options:{
        customBodyRenderLite: (dataIndex) => {
          const row = users[dataIndex]; 
          return (
            <Box display="flex" flexWrap="wrap" >
              {row.roles.map((role) => (
                <Chip key={role.id} label={role.name} color="secondary" size="small" className={helperClasses.marginRight} />
              ))}
            </Box>
          );
        }
        
      }},
      {name:"inviteStatus", label:"Password", options:{
        customBodyRenderLite: (dataIndex) => {
          const row = users[dataIndex];
          const status = INVITE_LABELS[row.inviteStatus] || INVITE_LABELS.none;
          return <Chip label={status.label} color={status.color} size="small" />;
        }
      }},
      {name: "", options: {
      filter: false,
      sort: false,
      empty: true,
      customBodyRenderLite: (dataIndex) => {
        const row = users[dataIndex]; 
        // Invitations only apply until a password exists. Once it does the user
        // owns their own credentials and goes through password reset instead.
        const canInvite = !row.hasPassword && !!row.active;
        const invitePending = pendingInviteIds.indexOf(row.id) > -1;
        return (
          <Box display="flex" justifyContent="flex-end">
            <Button variant="contained" size="small" color="warning" margin onClick={() => openDialog("Edit User" , <UpsertForm user={row} roles={assignableRoles} /> )} >Edit</Button>
            {canInvite && <Button variant="contained" size="small" color="primary" margin pending={invitePending} onClick={() => resendInvite(row)} >Resend Invite</Button>}
            {canInvite && <Button variant="outlined" size="small" color="primary" margin pending={invitePending} onClick={() => showInviteLink(row)} >Copy Link</Button>}
            {!!row.active && <Button variant="contained" size="small" color="error" margin onClick={() => openConfirmationDialog("Are you sure you want to disable " + row.email + " ?" , () => toggleUserStatus(row)  ,"Confirm Disabling" )} >Disable</Button>}
            {!row.active && <Button variant="contained" size="small" color="info" margin onClick={() => openConfirmationDialog("Are you sure you want to enable " + row.email + " ?" , () => toggleUserStatus(row)  ,"Confirm Enabling" )} >Enable</Button>}
          </Box>
        );
      }
    }
  }];

    //Entry point for admin-initiated signup. The table had no add button before,
    //so the only route into the system was self-registration plus approval.
    const options = {
      customToolbar: () => (
        <AddToolButton handleClick={() => openDialog("Invite User" , <UpsertForm roles={assignableRoles} /> )} />
      ),
    }

    return (
      <>
        <PageTitle title="Users" />
        <Grid container spacing={4}>
          <Grid item xs={12}>            
              <MUIDataTable loading={isLoading} error={isError} data={users ? users : []} columns={columns} options={options} />
          </Grid>
        </Grid>
      </>
    );
  }
